// Numeric input as typed on a tablet: a comma or a dot as the decimal
// separator, at most the field's decimals, a sign allowed.

export type ParsedNumber =
  | { kind: 'empty' }
  | { kind: 'incomplete' }
  | { kind: 'invalid'; reason: 'not_a_number' | 'decimals' }
  | { kind: 'value'; value: number; text: string };

const NUMBER = /^-?\d+(?:[.,](\d*))?$/;

export function parseNumericInput(raw: string, decimals: number | null): ParsedNumber {
  const text = raw.trim();
  if (text === '') return { kind: 'empty' };
  if (text === '-' || text === '.' || text === ',' || text === '-.' || text === '-,') {
    return { kind: 'incomplete' };
  }
  const match = NUMBER.exec(text);
  if (match === null) return { kind: 'invalid', reason: 'not_a_number' };
  const fraction = match[1];
  if (fraction === '' && text.endsWith('.')) return { kind: 'incomplete' };
  if (fraction === '' && text.endsWith(',')) return { kind: 'incomplete' };
  if (decimals !== null && fraction !== undefined && fraction.length > decimals) {
    return { kind: 'invalid', reason: 'decimals' };
  }
  const normalised = text.replace(',', '.');
  const value = Number(normalised);
  if (!Number.isFinite(value)) return { kind: 'invalid', reason: 'not_a_number' };
  return { kind: 'value', value, text: normalised };
}

/** The value as the field prints it: fixed decimals when the field has them. */
export function formatNumeric(value: number, decimals: number | null): string {
  return decimals === null ? String(value) : value.toFixed(decimals);
}

// D-103: input takes a comma or a dot; what a person reads carries the
// separator of the organization's locale, a comma in es-AR. What travels to
// the server keeps the dot, from `formatNumeric` above.

/** The decimal separator of a locale. */
export function decimalSeparator(locale: string): string {
  try {
    const part = new Intl.NumberFormat(locale)
      .formatToParts(1.5)
      .find((candidate) => candidate.type === 'decimal');
    return part?.value ?? '.';
  } catch {
    return '.';
  }
}

/** The value as a person reads it: the field's decimals, the locale's separator, no grouping. */
export function formatNumericDisplay(
  value: number,
  decimals: number | null,
  locale: string,
): string {
  return formatNumeric(value, decimals).replace('.', decimalSeparator(locale));
}

/** What was typed, digits untouched, its separator shown the locale's way. */
export function localizeNumericText(text: string, locale: string): string {
  return text.replace(/[.,]/, decimalSeparator(locale));
}
